import { z } from 'zod'
import { roundSchema, matchConfigSchema, purseSchema, stakeSchema, gameModeSchema } from './game.js'
import { roomCodeSchema, roomErrorCodeSchema } from './room.js'
import type { Room } from './room.js'

// ---------------------------------------------------------------------------
// Client -> Server payloads
// ---------------------------------------------------------------------------

export const createRoomInput = z.object({
  config: matchConfigSchema,
  isPublic: z.boolean().default(false),
})
export type CreateRoomInput = z.infer<typeof createRoomInput>

export const joinRoomInput = z.object({ code: roomCodeSchema })
export type JoinRoomInput = z.infer<typeof joinRoomInput>

export const setReadyInput = z.object({ ready: z.boolean() })
export type SetReadyInput = z.infer<typeof setReadyInput>

export const updateConfigInput = z.object({ config: matchConfigSchema })
export type UpdateConfigInput = z.infer<typeof updateConfigInput>

/**
 * The purse is part of the queue request, not an assumption.
 *
 * Without it the server rebuilt the config with the schema default, so a player
 * who chose a prize match was quietly queued for a practice one - staking the
 * wrong currency against an opponent who believed they were playing for money.
 */
export const joinQueueInput = z.object({
  mode: gameModeSchema,
  stake: stakeSchema,
  duration: z.number().int().positive(),
  purse: purseSchema,
})
export type JoinQueueInput = z.infer<typeof joinQueueInput>

/**
 * Starts a round against the bot. No lobby and no waiting.
 *
 * The ante is in practice coins, and the house puts up the bot's side, so a win
 * is worth something and a loss costs something. A stake of 0 is a free look at
 * a mode with nothing moving either way.
 */
export const startPracticeInput = z.object({
  mode: gameModeSchema,
  duration: z.number().int().positive(),
  stake: stakeSchema.default(0),
})
export type StartPracticeInput = z.infer<typeof startPracticeInput>

/**
 * Clicks are reported as millisecond offsets from round start, batched every
 * ~250ms. Offsets rather than absolute timestamps keep the payload small and
 * make the server's validation independent of clock skew.
 */
export const clickBatchInput = z.object({
  matchId: z.string(),
  offsets: z.array(z.number().int().min(0)).max(512),
})
export type ClickBatchInput = z.infer<typeof clickBatchInput>

export const answerInput = z.object({
  matchId: z.string(),
  roundIndex: z.number().int().min(1),
  value: z.number().int().min(0).max(9999),
})
export type AnswerInput = z.infer<typeof answerInput>

// ---------------------------------------------------------------------------
// Server -> Client payloads
// ---------------------------------------------------------------------------

export const roomErrorEvent = z.object({
  code: roomErrorCodeSchema,
  message: z.string(),
})
export type RoomErrorEvent = z.infer<typeof roomErrorEvent>

export const countdownEvent = z.object({
  /** Server epoch ms at which round one begins. */
  startsAt: z.number().int(),
  matchId: z.string(),
  pot: z.number().int(),
})
export type CountdownEvent = z.infer<typeof countdownEvent>

/**
 * The pre-round card. In Scatter mode this is where the player is told what to
 * count; it is deliberately a separate event from `round:display` so the client
 * can hold it on screen for a fixed beat before anything appears.
 */
export const briefEvent = z.object({
  roundIndex: z.number().int().min(1),
  targetShape: z.string(),
  targetLabel: z.string(),
  displayMs: z.number().int(),
  survivors: z.array(z.string()),
  /** Server epoch ms at which the display phase begins. */
  displayStartsAt: z.number().int(),
})
export type BriefEvent = z.infer<typeof briefEvent>

export const displayEvent = z.object({
  round: roundSchema,
  /** Server epoch ms the display window opened. */
  startedAt: z.number().int(),
})
export type DisplayEvent = z.infer<typeof displayEvent>

export const collectEvent = z.object({
  roundIndex: z.number().int().min(1),
  /** Server epoch ms after which answers are refused. */
  deadlineAt: z.number().int(),
})
export type CollectEvent = z.infer<typeof collectEvent>

export const playerRoundResult = z.object({
  userId: z.string(),
  answer: z.number().int().nullable(),
  correct: z.boolean(),
  answerMs: z.number().int().nullable(),
})
export type PlayerRoundResult = z.infer<typeof playerRoundResult>

export const roundResultEvent = z.object({
  roundIndex: z.number().int().min(1),
  correctAnswer: z.number().int(),
  results: z.array(playerRoundResult),
  eliminated: z.array(z.string()),
  survivors: z.array(z.string()),
  /** True when everyone matched — nobody goes out and the next round is harder. */
  allSameOutcome: z.boolean(),
  nextRoundAt: z.number().int().nullable(),
})
export type RoundResultEvent = z.infer<typeof roundResultEvent>

export const clickTickEvent = z.object({
  remainingMs: z.number().int().min(0),
  scores: z.array(z.object({ userId: z.string(), score: z.number().int() })),
})
export type ClickTickEvent = z.infer<typeof clickTickEvent>

export const MATCH_END_REASONS = [
  'normal',
  'overtime_split',
  'opponents_forfeited',
  'all_disconnected',
  'cheating_detected',
] as const
export const matchEndReasonSchema = z.enum(MATCH_END_REASONS)
export type MatchEndReason = z.infer<typeof matchEndReasonSchema>

export const matchEndEvent = z.object({
  matchId: z.string(),
  winnerIds: z.array(z.string()),
  pot: z.number().int(),
  payoutPerWinner: z.number().int(),
  /**
   * Which coins this match was played for.
   *
   * Carried on the event rather than read from the room, because the room is
   * closed thirty seconds before the result overlay is dismissed — and a result
   * that has to consult state which no longer exists will describe itself wrong
   * exactly when someone is still looking at it.
   */
  purse: purseSchema,
  /** Which mode was played, so the score suffix survives the room too. */
  mode: gameModeSchema,
  reason: matchEndReasonSchema,
  finalScores: z.array(
    z.object({
      userId: z.string(),
      /**
       * Carried here for the same reason as `purse`: the result outlives the
       * room, so anything the result needs to render has to travel with it
       * rather than be looked up in state that is being torn down.
       */
      displayName: z.string(),
      score: z.number().int(),
      eliminatedInRound: z.number().int().nullable(),
    }),
  ),
})
export type MatchEndEvent = z.infer<typeof matchEndEvent>

/**
 * A balance change. Names the purse it applies to, because a client holds both
 * and a bare number would silently overwrite the wrong one.
 */
export const walletEvent = z.object({
  purse: purseSchema,
  balance: z.number().int().min(0),
})
export type WalletEvent = z.infer<typeof walletEvent>

export const overtimeEvent = z.object({
  overtimeIndex: z.number().int().min(1),
  durationMs: z.number().int(),
  startsAt: z.number().int(),
  tiedUserIds: z.array(z.string()),
})
export type OvertimeEvent = z.infer<typeof overtimeEvent>

// ---------------------------------------------------------------------------
// Socket.IO typed event maps
// ---------------------------------------------------------------------------

export type Ack<T> = (response: { ok: true; data: T } | { ok: false; error: RoomErrorEvent }) => void

export interface ClientToServerEvents {
  'room:create': (input: CreateRoomInput, ack: Ack<{ room: Room }>) => void
  'room:join': (input: JoinRoomInput, ack: Ack<{ room: Room }>) => void
  'room:leave': (ack: Ack<null>) => void
  'room:ready': (input: SetReadyInput, ack: Ack<null>) => void
  'room:config': (input: UpdateConfigInput, ack: Ack<{ room: Room }>) => void
  'room:start': (ack: Ack<null>) => void
  'queue:join': (input: JoinQueueInput, ack: Ack<null>) => void
  'queue:leave': (ack: Ack<null>) => void
  'practice:start': (input: StartPracticeInput, ack: Ack<{ room: Room }>) => void
  'game:clicks': (input: ClickBatchInput) => void
  'game:answer': (input: AnswerInput, ack: Ack<null>) => void
  'net:ping': (clientSentAt: number, ack: (serverTime: number) => void) => void
}

export interface ServerToClientEvents {
  'room:state': (room: Room) => void
  'room:error': (error: RoomErrorEvent) => void
  'room:closed': (reason: string) => void
  'queue:matched': (room: Room) => void
  'match:countdown': (event: CountdownEvent) => void
  'round:brief': (event: BriefEvent) => void
  'round:display': (event: DisplayEvent) => void
  'round:collect': (event: CollectEvent) => void
  'round:result': (event: RoundResultEvent) => void
  'match:tick': (event: ClickTickEvent) => void
  'match:overtime': (event: OvertimeEvent) => void
  'match:end': (event: MatchEndEvent) => void
  'wallet:update': (event: WalletEvent) => void
}
